import {ChangeEvent, useRef, useState} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ReactComponent as CheckMarkIcon} from '../../../../../../assets/images/checkmark.svg';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import './UploadProtocol.scss';
import {useReviewHeaderContext} from '../../../header/ReviewHeader';
import {formatUtcTimestamp} from '../generateArtifacts/GenerateArtifacts';
import {ToastType} from '../../../../../../shared/constants';

const allowedFileTypes: string[] = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
];

const UploadProtocol = () => {
  const protocolItems = [
    {label: t('ai.review.protocolIntelligence.caseContext'), completed: true},
    {
      label: t('ai.review.protocolIntelligence.relevanceCriteria'),
      completed: true,
    },
    {label: t('ai.review.protocolIntelligence.issueCodes'), completed: true},
  ];

  const {
    setUploadedFile,
    setReviewHeaderToast,
    setUploadedAt,
    handlReviewHeaderToastClose,
  } = useReviewHeaderContext();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [openUploadErrorModal, setOpenUploadErrorModal] = useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);

  const handleUploadProtocol = () => {
    fileInputRef.current?.click();
  };

  const setFileErrorStates = () => {
    setOpenUploadErrorModal(true);
    setUploadedFile(null);
    setUploadedAt(null);
    handlReviewHeaderToastClose();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    // ========== Validation for file type and size (max 500MB) =========
    const maxSize = 500 * 1024 * 1024;
    if (!allowedFileTypes.includes(file.type)) {
      setFileErrorStates();
      event.target.value = '';
      return;
    }
    if (file.size > maxSize) {
      setFileSizeError(true);
      setFileErrorStates();
      event.target.value = '';
      return;
    }
    setUploadedFile(file);
    setUploadedAt(formatUtcTimestamp());
    setReviewHeaderToast({
      isOpen: true,
      message: t('ai.review.protocolIntelligence.uploadFileToastSuccess'),
      status: ToastType.Success,
    });
  };

  const handleErrorModalClose = () => {
    setFileSizeError(false);
    setOpenUploadErrorModal(false);
    setUploadedFile(null);
    handlReviewHeaderToastClose();
  };

  const handleTryAgain = () => {
    handleErrorModalClose();
    handleUploadProtocol();
  };

  return (
    <>
      <Box
        className="manage-protocol-content-body-text"
        aria-label={t('ai.review.protocolIntelligence.noProtocol')}
      >
        <Box
          className="manage-protocol-content-body-text-header"
          aria-label={t('ai.review.protocolIntelligence.noProtocol')}
          tabIndex={0}
        >
          {t('ai.review.protocolIntelligence.noProtocol')}
        </Box>
        <Box
          className="manage-protocol-content-body-text-subHeader"
          aria-label={t('ai.review.protocolIntelligence.uploadPrompt')}
          tabIndex={0}
        >
          {t('ai.review.protocolIntelligence.uploadPrompt')}
        </Box>
      </Box>
      <Box
        className="manage-protocol-content-body-text-items"
        aria-label="protocol-items"
      >
        {protocolItems.map((item, index) => (
          <Box
            className="manage-protocol-content-body-text-item"
            key={index}
            aria-label={item.label}
          >
            <Box
              className="manage-protocol-content-body-text-label"
              aria-label={item.label}
              tabIndex={0}
            >
              {item.label}
            </Box>
            <Box aria-label="protocol-item-status" tabIndex={0}>
              {item.completed && (
                <IconButton
                  disabled
                  tabIndex={0}
                  aria-label={`${item.label}-completed`}
                >
                  <CheckMarkIcon />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <Box
        className="upload-protocol"
        aria-label={t('ai.review.protocolIntelligence.uploadProtocol')}
      >
        <ArrButton
          className="upload-protocol-button"
          onClick={handleUploadProtocol}
          size="medium"
          variant="primary"
          aria-label={t('ai.review.protocolIntelligence.uploadProtocol')}
          tabIndex={0}
          dataTestId="upload-protocol-upload-protocol-button"
        >
          {t('ai.review.protocolIntelligence.uploadProtocol')}
        </ArrButton>

        <Box
          className="upload-file-types"
          mt={2}
          aria-label={t('ai.review.protocolIntelligence.uploadFileTypes')}
          tabIndex={0}
        >
          {t('ai.review.protocolIntelligence.uploadFileTypes')}
        </Box>

        <input
          type="file"
          ref={fileInputRef}
          style={{display: 'none'}}
          accept=".doc,.docx,.pdf"
          onChange={handleFileChange}
          aria-label={t('ai.review.protocolIntelligence.uploadProtocol')}
          tabIndex={0}
          data-testid={`upload-protocol-input`}
        />
      </Box>
      {openUploadErrorModal && (
        <ArrModal
          showClose={false}
          showModal={openUploadErrorModal}
          onClose={handleErrorModalClose}
          title={
            fileSizeError
              ? t(
                  'ai.review.protocolIntelligence.uploadErrorModalFileSizeTitle'
                )
              : t('ai.review.protocolIntelligence.uploadErrorModalTitle')
          }
          aria-label="upload-protocol-error-modal"
          dataTestId="arr-upload-protocol-error-modal"
          buttons={[
            {
              id: t('common.close'),
              label: t('common.close'),
              onClick: handleErrorModalClose,
              variant: 'secondary',
              dataTestId: 'modal-no',
            },
            {
              id: t('common.tryAgain'),
              label: t('common.tryAgain'),
              onClick: handleTryAgain,
              variant: 'primary',
              dataTestId: 'modal-yes',
            },
          ]}
        >
          <Typography
            className="upload-protocol-error-description"
            aria-label={
              fileSizeError
                ? t(
                    'ai.review.protocolIntelligence.uploadErrorModalFileSizeDescription'
                  )
                : t(
                    'ai.review.protocolIntelligence.uploadErrorModalDescription'
                  )
            }
            tabIndex={0}
            data-testid={`upload-protocol-description${fileSizeError ? '-error' : ''}`}
          >
            {fileSizeError
              ? t(
                  'ai.review.protocolIntelligence.uploadErrorModalFileSizeDescription'
                )
              : t('ai.review.protocolIntelligence.uploadErrorModalDescription')}
          </Typography>
        </ArrModal>
      )}
    </>
  );
};

export default UploadProtocol;
